import React, { useContext, useEffect, useState } from "react";
import "./OperationControlsPage.css";
import { ModalContext } from "../../store/ModalContext";
import { SeasonContext } from "../../store/SeasonContext";
import { ApplicationPeriod } from "../../../../api/season";
import { SubscriptionsContext } from "../../store/SubscriptionsContext";
import NavigateSeasonsButton from "./components/NavigateSeasonsButton";

function getApplicationPeriodLabel(period: ApplicationPeriod): string {
  if (period === ApplicationPeriod.PreviousCSA) return "Previous CSA";
  if (period === ApplicationPeriod.Waitlisted) return "Waitlisted";
  if (period === ApplicationPeriod.FreeForAll) return "Free For All";
  return "Application Closed";
}

const OperationControlsPage = () => {
  const {
    applicationPeriod,
    areWeInSeason,
    updateApplicationPeriod,
    loading,
    error,
    isBusinessOpen,
    updateIsBusinessOpen,
    freeDeliveryThreshold,
    businessDeliveryPrice,
    csaDeliveryPrice,
    updateShippingPricing,
    updateCSADeliveryPrice,
  } = useContext(SeasonContext);
  const [thresholdInput, setThresholdInput] = useState("");
  const [shippingPriceInput, setShippingPriceInput] = useState("");
  const [csaDeliveryPriceInput, setCSADeliveryPriceInput] = useState("");
  const { subscriptions, updateSubscription } =
    useContext(SubscriptionsContext);
  const { openModal, closeModal } = useContext(ModalContext);

  function handleApplicationPeriodChange(period: ApplicationPeriod) {
    if (!areWeInSeason) {
      openModal({
        title: "WARNING",
        body: "You have to start the season before you can open the application period.",
        showCloseButton: true,
      });
      return;
    }
    const periodLabel = getApplicationPeriodLabel(period);

    if (
      applicationPeriod === ApplicationPeriod.ApplicationClosed &&
      period !== ApplicationPeriod.ApplicationClosed
    ) {
      openModal({
        title: "WARNING",
        body: (
          <p>
            Are you sure you want to change the application period to "
            <strong>{periodLabel}</strong>"?
          </p>
        ),
        buttons: [
          { text: "No", callback: closeModal },
          {
            text: "Yes, Change It",
            loadingText: "Saving...",
            callback: () => {
              updateApplicationPeriod(period);
              closeModal();
            },
          },
        ],
      });
      return;
    }

    openModal({
      title: "WARNING",
      body: `Are you sure you want to change the application period to "${periodLabel}"?`,
      buttons: [
        { text: "No", callback: closeModal },
        {
          text: "Yes",
          loadingText: "Saving...",
          callback: () => {
            updateApplicationPeriod(period);
            closeModal();
          },
        },
      ],
    });
  }
  const whoCanApply = () => {
    if (applicationPeriod === ApplicationPeriod.PreviousCSA) {
      return "previous CSA members";
    }
    if (applicationPeriod === ApplicationPeriod.Waitlisted) {
      return "previous CSA members and waitlisted people";
    }
    if (applicationPeriod === ApplicationPeriod.FreeForAll) {
      return "everyone";
    }

    return "no one";
  };
  const orderPlacementStatus = isBusinessOpen ? "can" : "can't";

  function handleBusinessOpenChange(open: boolean) {
    const statusLabel = open ? "opened" : "closed";
    openModal({
      title: "WARNING",
      body: `Are you sure you want to set business to "${statusLabel}"?`,
      buttons: [
        { text: "No", callback: closeModal },
        {
          text: "Yes",
          loadingText: "Saving...",
          callback: async () => {
            await updateIsBusinessOpen(open);
            closeModal();
          },
        },
      ],
    });
  }

  useEffect(() => {
    setThresholdInput(String(freeDeliveryThreshold));
    setShippingPriceInput(String(businessDeliveryPrice));
    setCSADeliveryPriceInput(String(csaDeliveryPrice));
  }, [freeDeliveryThreshold, businessDeliveryPrice, csaDeliveryPrice]);

  function handleSubscriptionActiveChange(subscriptionId: string) {
    const subscription = subscriptions.find((s) => s.id === subscriptionId);
    if (!subscription) return;
    void updateSubscription(subscriptionId, {
      isActive: !(subscription.isActive ?? false),
    });
  }

  function handleSaveShippingPricing() {
    const threshold = Number(thresholdInput);
    const price = Number(shippingPriceInput);
    if (!Number.isFinite(threshold) || threshold < 0) {
      openModal({
        title: "WARNING",
        body: "Free shipping threshold must be a non-negative number.",
        showCloseButton: true,
      });
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      openModal({
        title: "WARNING",
        body: "Shipping price must be a non-negative number.",
        showCloseButton: true,
      });
      return;
    }
    openModal({
      title: "WARNING",
      body: `Are you sure you want to set free shipping threshold to ${threshold} kr and shipping price to ${price} kr?`,
      buttons: [
        { text: "No", callback: closeModal },
        {
          text: "Yes",
          loadingText: "Saving...",
          callback: () => {
            updateShippingPricing(threshold, price);
            closeModal();
          },
        },
      ],
    });
  }

  function handleSaveCSADeliveryPrice() {
    const price = Number(csaDeliveryPriceInput);
    if (!Number.isFinite(price) || price < 0) {
      openModal({
        title: "WARNING",
        body: "CSA delivery price must be a non-negative number.",
        showCloseButton: true,
      });
      return;
    }
    openModal({
      title: "WARNING",
      body: `Are you sure you want to set CSA delivery price to ${price} kr?`,
      buttons: [
        { text: "No", callback: closeModal },
        {
          text: "Yes",
          loadingText: "Saving...",
          callback: () => {
            updateCSADeliveryPrice(price);
            closeModal();
          },
        },
      ],
    });
  }

  return (
    <div className="page-container">
      <h1>Operation Controls</h1>
      <div className="page-content">
        {error && <p>{error}</p>}
        <div>
          <h3 className="operation-controls-section-title">
            Application Period Controls
          </h3>
          <p className="operation-controls-info-text">
            Right now <span className="bold">{whoCanApply()}</span> can apply to
            become CSA members.
          </p>
          <div className="operation-controls-buttons">
            <button
              onClick={() =>
                handleApplicationPeriodChange(ApplicationPeriod.PreviousCSA)
              }
              className={
                applicationPeriod === ApplicationPeriod.PreviousCSA
                  ? "btn operation-controls-btn active-operation-controls-btn"
                  : "btn operation-controls-btn"
              }
              disabled={
                loading || applicationPeriod === ApplicationPeriod.PreviousCSA
              }
            >
              Previous CSA
            </button>
            <button
              onClick={() =>
                handleApplicationPeriodChange(ApplicationPeriod.Waitlisted)
              }
              className={
                applicationPeriod === ApplicationPeriod.Waitlisted
                  ? "btn operation-controls-btn active-operation-controls-btn"
                  : "btn operation-controls-btn"
              }
              disabled={
                loading || applicationPeriod === ApplicationPeriod.Waitlisted
              }
            >
              Waitlisted
            </button>
            <button
              onClick={() =>
                handleApplicationPeriodChange(ApplicationPeriod.FreeForAll)
              }
              className={
                applicationPeriod === ApplicationPeriod.FreeForAll
                  ? "btn operation-controls-btn active-operation-controls-btn"
                  : "btn operation-controls-btn"
              }
              disabled={
                loading || applicationPeriod === ApplicationPeriod.FreeForAll
              }
            >
              Free For All
            </button>
            <button
              onClick={() =>
                handleApplicationPeriodChange(
                  ApplicationPeriod.ApplicationClosed,
                )
              }
              className={
                applicationPeriod === ApplicationPeriod.ApplicationClosed
                  ? "btn operation-controls-btn active-operation-controls-btn"
                  : "btn operation-controls-btn"
              }
              disabled={
                loading ||
                applicationPeriod === ApplicationPeriod.ApplicationClosed
              }
            >
              Application Closed
            </button>
          </div>
        </div>
        <div className="orders-allowed-container">
          <h3 className="operation-controls-section-title">
            Business Order Controls
          </h3>
          <p className="operation-controls-info-text">
            Right now Business Accounts{" "}
            <span className="bold">{orderPlacementStatus}</span> place orders.
          </p>
          <div className="operation-controls-buttons">
            <button
              onClick={() => handleBusinessOpenChange(true)}
              className={
                isBusinessOpen
                  ? "btn operation-controls-btn active-operation-controls-btn"
                  : "btn operation-controls-btn"
              }
              disabled={loading || isBusinessOpen}
            >
              Business opened
            </button>
            <button
              onClick={() => handleBusinessOpenChange(false)}
              className={
                !isBusinessOpen
                  ? "btn operation-controls-btn active-operation-controls-btn"
                  : "btn operation-controls-btn"
              }
              disabled={loading || !isBusinessOpen}
            >
              Business Closed
            </button>
          </div>
        </div>
        <div className="season-controls-container">
          <h3 className="operation-controls-section-title">Season Controls</h3>
          <p className="operation-controls-info-text">
            Right now we are{" "}
            <span className="bold">
              {areWeInSeason ? "in season" : "not in season"}
            </span>
            . During the season you can manage the application periods and the
            avtivation and inactivation of subscriptions. During off season you
            can edit pickup locations, subscriptions, tiers, and delivery price.
          </p>
          <p className="operation-controls-info-text">
            <span className="bold">
              Never end the season until after the last subscription is
              delivered. When you end the season, all CSA accounts get
              downgraded to social accounts. This means the delivery list will
              be deleted and CSA members lose access to their dashboard.
            </span>
          </p>

          <NavigateSeasonsButton loading={loading} />
        </div>
        <div className="season-controls-container">
          <h3 className="operation-controls-section-title">
            Subscription Controls
          </h3>
          <p className="operation-controls-info-text">
            Here you can choose which subscriptions are currently active and
            ready for delivery. This will also enable the weekly emails to be
            sent to the CSA members on theirpickup day. <br />
            <strong>
              Combo Subscriptions should be enabled if one of the two
              subscriptions is active.
            </strong>
          </p>

          <ul className="subscription-controls-list">
            {subscriptions.map((subscription) => (
              <li
                key={subscription.id}
                className="info-card tier-info-card subscription-control-card"
              >
                <div className="subscription-control-name-container">
                  <span className="subscription-control-name">
                    {subscription.name}
                  </span>
                  <span
                    className={subscription.isActive ? "tag-green" : "tag-red"}
                  >
                    {subscription.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <input
                  type="checkbox"
                  checked={subscription.isActive ?? false}
                  onChange={() =>
                    handleSubscriptionActiveChange(subscription.id)
                  }
                  disabled={!areWeInSeason || loading}
                />
              </li>
            ))}
          </ul>
        </div>{" "}
        <div className="orders-allowed-container">
          <h3 className="operation-controls-section-title">
            Business Delivery Price Controls
          </h3>
          <p className="operation-controls-info-text">
            Deliveries for orders are free when the subtotal is at least{" "}
            <span className="bold">{freeDeliveryThreshold} kr</span>. Below
            that, the delivery price is{" "}
            <span className="bold">{businessDeliveryPrice} kr</span>.
          </p>
          <div className="operation-controls-buttons shipping-regulation-container">
            <div className="operation-controls-input-pairs-container">
              <div className="operation-controls-input-pair">
                <label htmlFor="free-shipping-threshold">
                  Free Delivery Threshold
                </label>

                <input
                  id="free-shipping-threshold"
                  type="number"
                  min={0}
                  value={thresholdInput}
                  onChange={(e) => setThresholdInput(e.target.value)}
                  placeholder="Free shipping threshold (kr)"
                />
              </div>{" "}
              <div className="operation-controls-input-pair">
                <label htmlFor="shipping-cost">Delivery Price</label>

                <input
                  type="number"
                  id="shipping-cost"
                  min={0}
                  value={shippingPriceInput}
                  onChange={(e) => setShippingPriceInput(e.target.value)}
                  placeholder="Shipping price (kr)"
                />
              </div>
            </div>
            <button
              className="btn operation-controls-btn"
              onClick={handleSaveShippingPricing}
              disabled={loading}
            >
              Save delivery price
            </button>
          </div>
        </div>
        <div className="orders-allowed-container">
          <h3 className="operation-controls-section-title">
            CSA Delivery Price Controls
          </h3>
          <p className="operation-controls-info-text">
            The current price for CSA deliveries is{" "}
            <span className="bold">{csaDeliveryPrice} kr</span>. CSA members who
            choose pickup at the farm as default will not be charged this price.
          </p>
          <div className="operation-controls-buttons shipping-regulation-container csa-shipping-regulation-container">
            <div className="operation-controls-input-pair">
              <label htmlFor="csa-delivery-price">CSA Delivery Price</label>

              <input
                id="csa-delivery-price"
                type="number"
                min={0}
                value={csaDeliveryPriceInput}
                onChange={(e) => setCSADeliveryPriceInput(e.target.value)}
                placeholder="CSA delivery price (kr)"
                disabled={areWeInSeason}
              />
            </div>{" "}
            <button
              className="btn operation-controls-btn"
              onClick={handleSaveCSADeliveryPrice}
              disabled={loading || areWeInSeason}
            >
              Save CSA Delivery Price
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationControlsPage;
